// Dependencies: cart-drawer-essentials.js

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    // Check for post-checkout cleanup on initialization
    this.handlePostCheckoutCleanup();

    // Check if cart should be opened after page refresh (e.g., after adding package or gift card)
    this.checkOpenCartAfterRefresh();

    // Add clear cart button listener
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-clear-button]')) {
        this.handleClearCart();
      }
    });

    // Add checkout button listener to track checkout attempts
    this.addEventListener('click', (event) => {
      const checkoutButton = event.target.closest('#CartDrawer-Checkout');
      if (checkoutButton && !checkoutButton.disabled) {
        this.handleCheckoutAttempt();
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    // Task 12 (per-week delivery availability): a stale delivery date can go
    // undetected for as long as the tab sits idle after page load -- the
    // DOMContentLoaded revalidation in assets/delivery-date.js only runs once, at
    // load. Re-check right as the drawer is about to open, since that's the moment a
    // returning customer is most likely headed for Checkout.
    if (window.StockedDelivery && typeof window.StockedDelivery.revalidate === 'function') {
      try {
        window.StockedDelivery.revalidate();
      } catch (err) {
        console.error('CartDrawer: StockedDelivery.revalidate() failed', err);
      }
      // revalidate() can synchronously open one of two dialogs: the BLOCKING
      // delivery gate (stale date -- see openGate() in assets/delivery-date.js) or
      // Task 13's reconcile dialog (the stored date is fine, but some cart line no
      // longer fits its week). Don't open the drawer underneath EITHER: the drawer
      // is z-indexed above both (it's persistent chrome, not another transient
      // modal), so stacking it on top would bury the dialog's buttons, and two
      // simultaneous focus-trapped surfaces is a genuine conflict regardless.
      // Checked directly off DOM state (hasOpenDialog()) rather than off
      // revalidate()'s return value, and NOT scoped to data-shop-surface -- the
      // reconcile dialog can open on ANY page (e.g. /cart itself is not a shop
      // surface, but is exactly where this is likeliest to fire), unlike the
      // blocking gate, which only ever opens on shop surfaces on its own.
      if (
        typeof window.StockedDelivery.hasOpenDialog === 'function' &&
        window.StockedDelivery.hasOpenDialog()
      ) {
        return;
      }
    }

    // Clear the flag immediately to prevent reopening on subsequent page loads
    localStorage.removeItem('openCartAfterRefresh');

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // here the animation doesn't seem to always get triggered
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, { open = true } = {}) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // Update essentials with new cart data
    const essentialsElement = this.querySelector('cart-drawer-essentials');
    if (essentialsElement) {
      essentialsElement.fetchRecommendedProducts();
    }

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (open) {
        this.open();
      } else {
        // Silent add: confirm without stealing focus / covering the menu.
        this.announceItemAdded();
        this.bounceCartIcon();
      }
    });
  }

  announceItemAdded() {
    const region = document.getElementById('cart-live-region');
    if (!region) return;
    const message = (window.variantStrings && window.variantStrings.addedAnnouncement) || 'Item added to cart';
    // Clear first, then set on the next frame so a repeated add is still announced.
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }

  bounceCartIcon() {
    // #cart-icon-bubble's innerHTML was just replaced, so query the fresh bubble.
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (!bubble) return;
    bubble.classList.remove('cart-count-bubble--bounce');
    void bubble.offsetWidth; // force reflow so the animation restarts on repeat adds
    bubble.classList.add('cart-count-bubble--bounce');
    bubble.addEventListener(
      'animationend',
      () => bubble.classList.remove('cart-count-bubble--bounce'),
      { once: true }
    );
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  checkOpenCartAfterRefresh() {
    // Check if we should open the cart after a page refresh
    const shouldOpenCart = localStorage.getItem('openCartAfterRefresh');

    if (shouldOpenCart === 'true') {
      // Open the cart after a short delay to ensure the page is fully loaded
      setTimeout(() => {
        this.open();
      }, 150);
    }
  }

  handlePostCheckoutCleanup() {
    const checkoutAttempted = localStorage.getItem('checkout_attempted');
    
    if (checkoutAttempted) {
      // Get current cart state from Shopify's global cart object or fetch it
      this.checkCartState().then(cartEmpty => {
        if (cartEmpty) {
          // Cart is empty and checkout was attempted - assume successful checkout
          this.clearPostCheckoutData();
        } else {
          // Cart has items but checkout was attempted - user likely abandoned checkout
          localStorage.removeItem('checkout_attempted');
          localStorage.removeItem('checkout_timestamp');
        }
      }).catch(error => {
        console.error('Error checking cart state:', error);
        // If we can't check cart state, clear old flags based on timestamp
        const checkoutTimestamp = localStorage.getItem('checkout_timestamp');
        const timestampAge = checkoutTimestamp ? Date.now() - parseInt(checkoutTimestamp) : 0;
        if (timestampAge > 60 * 60 * 1000) { // 1 hour
          localStorage.removeItem('checkout_attempted');
          localStorage.removeItem('checkout_timestamp');
        }
      });
    }
  }

  async checkCartState() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      return cart.item_count === 0;
    } catch (error) {
      // Fallback: check if global cart object exists
      if (window.cart) {
        return window.cart.item_count === 0;
      }
      throw error;
    }
  }

  handleCheckoutAttempt() {
    // The delivery date is enforced via the disabled checkout button (set server-side
    // when cart.attributes.delivery_date is blank, kept in sync by <cart-delivery-date>).
    // Here we only record the attempt for post-checkout cleanup.
    localStorage.setItem('checkout_attempted', 'true');
    localStorage.setItem('checkout_timestamp', Date.now().toString());
  }

  clearPostCheckoutData() {
    // Clear checkout tracking
    localStorage.removeItem('checkout_attempted');
    localStorage.removeItem('checkout_timestamp');

    // Clear the validated zip; also resets the cart field + free-shipping bar via zipcode:cleared.
    window.StockedDeliveryZones?.clearZip();
  }

  async handleClearCart() {
    const clearButton = this.querySelector('[data-cart-clear-button]');
    const buttonText = clearButton?.querySelector('.cart-drawer__clear-cart-text');

    if (clearButton && buttonText) {
      // Update button to loading state
      clearButton.disabled = true;
      buttonText.textContent = 'Clearing cart...';
    }

    try {
      await this.clearCartDirect();
    } catch (error) {
      console.error('Error clearing cart:', error);

      // Show user-friendly error message
      alert('There was an issue clearing your cart. Please try again.');
    } finally {
      // Reset button state on error
      if (clearButton && buttonText) {
        clearButton.disabled = false;
        buttonText.textContent = 'Clear Cart';
      }
    }
  }

  async clearCartDirect() {
    return new Promise((resolve, reject) => {
      fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })
      .then(response => {
        if (response.ok) {
          location.reload();
          resolve();
        } else {
          reject(new Error('Failed to clear cart'));
        }
      })
      .catch(reject);
    });
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer-CartItems',
        section: 'cart-drawer',
        selector: '.drawer__contents.js-contents',
      },
      {
        id: 'CartDrawer-DeliveryDate',
        section: 'cart-drawer',
        selector: '#CartDrawer-DeliveryDate',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'CartDrawer-Footer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
